import { useCallback, useEffect, useMemo, useState } from 'react'
import * as api from 'api/entityApi'
import {
  initialCustomFieldValues,
  toCustomFieldPayload,
  type CustomFieldValueMap,
} from 'components/edit/CustomFields'
import type {
  CustomFieldDef,
  Enquiry,
  EnquirySavePayload,
  Lead,
  LeadSavePayload,
  Option,
} from 'types/entity'
import { validateCustomFields, validateEnquiry, validateLead } from 'validation/entityValidation'

export interface EditFormValues {
  personName: string
  companyName: string
  mobiles: [string, string, string]
  emails: [string, string, string]
  countryId: string
  stateId: string
  cityId: string
  pinCode: string
  source: string
  medium: string
  campaign: string
  residentialAddress: string
  officialAddress: string
  remarks: string
  tags: string[]
}

const EMPTY_FORM: EditFormValues = {
  personName: '',
  companyName: '',
  mobiles: ['', '', ''],
  emails: ['', '', ''],
  countryId: '',
  stateId: '',
  cityId: '',
  pinCode: '',
  source: '',
  medium: '',
  campaign: '',
  residentialAddress: '',
  officialAddress: '',
  remarks: '',
  tags: [],
}

/**
 * All of the edit screen's state in one hook: reference lists, the record, the
 * custom fields, cascading location dropdowns, validation and save.
 *
 * The legacy `BindLeadDetails` matched the saved country/state/city *by name*
 * against the loaded option lists and appended a synthetic option when the name
 * was missing; `resolveOption` does the same so no value is silently lost.
 */
export function useEntityEditForm(entityType: 'lead' | 'enquiry', entityId: string) {
  const [form, setForm] = useState<EditFormValues>(EMPTY_FORM)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [errors, setErrors] = useState<string[]>([])

  const [countries, setCountries] = useState<Option[]>([])
  const [states, setStates] = useState<Option[]>([])
  const [cities, setCities] = useState<Option[]>([])
  const [sources, setSources] = useState<Option[]>([])
  const [mediums, setMediums] = useState<Option[]>([])
  const [campaigns, setCampaigns] = useState<Option[]>([])

  const [customFieldDefs, setCustomFieldDefs] = useState<CustomFieldDef[]>([])
  const [customFieldValues, setCustomFieldValues] = useState<CustomFieldValueMap>({})
  /** fieldId → parent-value rules, for custom fields whose choices depend on another field. */
  const [dependencyMap, setDependencyMap] = useState<Map<number, api.DependencyRule[]>>(new Map())

  /** Kept so the save payload can echo back fields the form does not edit. */
  const [record, setRecord] = useState<Lead | Enquiry | null>(null)

  useEffect(() => {
    if (!entityId) return
    const controller = new AbortController()
    const { signal } = controller
    let active = true

    async function load() {
      setLoading(true)
      setLoadError(null)
      try {
        const [countryList, sourceList, mediumList, campaignList, entity, fieldDefs] = await Promise.all([
          api.fetchCountries(signal),
          api.fetchSources(signal),
          api.fetchMediums(signal),
          api.fetchCampaigns(signal),
          entityType === 'lead' ? api.fetchLead(entityId, signal) : api.fetchEnquiry(entityId, signal),
          api.fetchEditCustomFields(entityType, entityId, signal),
        ])
        if (!active) return

        // The API answers "success" with an empty record for an id that does not
        // exist or belongs to another account. Loading an empty form over that
        // would let a save overwrite nothing (or the wrong thing), so stop here
        // and let the page show why.
        if (!entity) {
          setLoadError(
            `This ${entityType} could not be opened (id ${entityId}). It does not exist, or it belongs to a different account than the one you are signed in to.`,
          )
          return
        }

        setCountries(countryList)
        setSources(sourceList)
        setMediums(mediumList)
        setCampaigns(campaignList)
        setRecord(entity)
        setCustomFieldDefs(fieldDefs)
        setCustomFieldValues(initialCustomFieldValues(fieldDefs))

        // Only fields that declare a parent need dependency rules.
        const childFieldIds = fieldDefs
          .filter((def) => Number(def.ParentFieldId ?? 0) > 0)
          .map((def) => def.field_ID)
        if (childFieldIds.length > 0) {
          const rules = await api.fetchDependentFieldData(childFieldIds, signal)
          if (!active) return
          setDependencyMap(api.buildDependencyMap(rules))
        }

        // The detail response carries CountryId / StateId / CityId directly, so
        // the ids are used when present and matching on name is only a fallback
        // for older payloads that omit them.
        const country =
          findById(countryList, entity.countryId) ?? resolveOption(countryList, entity.country)
        const countryId = country?.id ?? entity.countryId ?? ''

        const stateList = countryId ? await api.fetchStates(countryId, signal) : []
        if (!active) return
        const state = findById(stateList, entity.stateId) ?? resolveOption(stateList, entity.state)
        const stateId = state?.id ?? entity.stateId ?? ''

        const cityList = stateId ? await api.fetchCities(stateId, signal) : []
        if (!active) return
        const city = findById(cityList, entity.cityId) ?? resolveOption(cityList, entity.city)

        // Append the stored name when the lookup list does not contain it.
        setStates(withFallback(stateList, entity.state, state))
        setCities(withFallback(cityList, entity.city, city))

        // The record carries source/medium/campaign as display names, but
        // usp_EditLeadDetails takes numeric ids — resolve each name to its
        // option id (falling back to the raw value for already-numeric data).
        const byName = (options: Option[], value: string) =>
          options.find((o) => o.name.toLowerCase() === (value ?? '').toLowerCase())?.id ?? value ?? ''

        setForm({
          personName: entity.personName,
          companyName: entity.companyName,
          mobiles: [entity.mobileNo, entity.mobileNo1, entity.mobileNo2],
          emails: [entity.emailId, entity.emailId1, entity.emailId2],
          countryId,
          stateId,
          cityId: city?.id ?? entity.cityId ?? '',
          pinCode: entity.pinCode,
          source: byName(sourceList, entity.source),
          medium: byName(mediumList, entity.medium),
          campaign: byName(campaignList, entity.campaign),
          residentialAddress: entity.residentialAddress,
          officialAddress: entity.officialAddress,
          remarks: 'initialRemarks' in entity ? entity.initialRemarks : entity.remarks,
          tags: entity.tags,
        })
      } catch (err) {
        if (!active || (err as Error).name === 'AbortError') return
        setLoadError((err as Error).message)
      } finally {
        if (active) setLoading(false)
      }
    }

    void load()
    return () => {
      active = false
      controller.abort()
    }
  }, [entityType, entityId])

  const setField = useCallback(<K extends keyof EditFormValues>(key: K, value: EditFormValues[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }))
  }, [])

  const setMobile = useCallback((index: 0 | 1 | 2, value: string) => {
    setForm((prev) => {
      const mobiles = [...prev.mobiles] as [string, string, string]
      mobiles[index] = value
      return { ...prev, mobiles }
    })
  }, [])

  const setEmail = useCallback((index: 0 | 1 | 2, value: string) => {
    setForm((prev) => {
      const emails = [...prev.emails] as [string, string, string]
      emails[index] = value
      return { ...prev, emails }
    })
  }, [])

  const setCustomField = useCallback((fieldId: number, value: string | string[] | boolean) => {
    setCustomFieldValues((prev) => ({ ...prev, [fieldId]: value }))
  }, [])

  /** Country change reloads states and clears the downstream selections. */
  const changeCountry = useCallback(async (countryId: string) => {
    setForm((prev) => ({ ...prev, countryId, stateId: '', cityId: '' }))
    setCities([])
    setStates(countryId ? await api.fetchStates(countryId) : [])
  }, [])

  const changeState = useCallback(async (stateId: string) => {
    setForm((prev) => ({ ...prev, stateId, cityId: '' }))
    setCities(stateId ? await api.fetchCities(stateId) : [])
  }, [])

  const nameOf = useCallback((options: Option[], id: string) => {
    return options.find((o) => o.id === id)?.name ?? id
  }, [])

  const save = useCallback(async (): Promise<string | null> => {
    const validationInput = {
      personName: form.personName.trim(),
      mobileNo: form.mobiles[0].trim(),
      mobileNo1: form.mobiles[1].trim(),
      mobileNo2: form.mobiles[2].trim(),
      emailId: form.emails[0].trim(),
      emailId1: form.emails[1].trim(),
      emailId2: form.emails[2].trim(),
      source: form.source,
      pinCode: form.pinCode.trim(),
    }

    const found = [
      ...(entityType === 'lead' ? validateLead(validationInput) : validateEnquiry(validationInput)),
      ...validateCustomFields(customFieldDefs, customFieldValues),
    ]

    setErrors(found)
    if (found.length > 0) return null

    setSaving(true)
    try {
      const common = {
        PersonName: validationInput.personName,
        CompanyName: form.companyName,
        MobileNo: validationInput.mobileNo,
        MobileNo1: validationInput.mobileNo1,
        MobileNo2: validationInput.mobileNo2,
        EmailID: validationInput.emailId,
        EmailID1: validationInput.emailId1,
        EmailID2: validationInput.emailId2,
        City: nameOf(cities, form.cityId),
        State: nameOf(states, form.stateId),
        Country: nameOf(countries, form.countryId),
        // The legacy code sent 0 for a blank pincode rather than an empty string.
        PinCode: validationInput.pinCode === '' ? 0 : validationInput.pinCode,
        SourceName: form.source,
        MediumName: form.medium || '0',
        CampaignName: form.campaign || '0',
        ResidentialAddress: form.residentialAddress,
        OfficialAddress: form.officialAddress,
        lstCustomField: toCustomFieldPayload(customFieldValues),
      }

      if (entityType === 'lead') {
        const lead = record as Lead | null
        const payload: LeadSavePayload = {
          ...common,
          LeadId: entityId,
          LeadNo: lead?.leadNo ?? '',
          CountryCode: '',
          CountryCode1: '',
          CountryCode2: '',
          AssignedTo: 0,
          ParentID: 0,
          FollowUpStatus: 0,
          AmountPaid: 0,
          CreatedBy: 0,
          ModifiedBy: 0,
          InitialRemarks: form.remarks,
          NextStatusDate: '',
          Remarks: '',
          Products: '',
          CreatedOn: '',
          ModifiedOn: '',
          FollowupDate: '',
          LeadImage: '',
          Img_ext: '',
          ProfileImgFileName: '',
          WhereAddress: '',
          WhereLatitude: '',
          WhereLongitude: '',
          LeadTags: form.tags.join(','),
        }
        return await api.saveLead(payload)
      }

      const payload: EnquirySavePayload = {
        ...common,
        EnquiryId: entityId,
        Remarks: form.remarks,
        EnquiryTags: form.tags.join(','),
      }
      return await api.saveEnquiry(payload)
    } finally {
      setSaving(false)
    }
  }, [
    form,
    entityType,
    entityId,
    record,
    customFieldDefs,
    customFieldValues,
    cities,
    states,
    countries,
    nameOf,
  ])

  const tableName = useMemo(() => (entityType === 'lead' ? 'Userleads' : 'UserEnquiry'), [entityType])

  return {
    form,
    setField,
    setMobile,
    setEmail,
    changeCountry,
    changeState,
    countries,
    states,
    cities,
    sources,
    mediums,
    campaigns,
    customFieldDefs,
    customFieldValues,
    setCustomField,
    dependencyMap,
    record,
    tableName,
    loading,
    saving,
    loadError,
    errors,
    save,
  }
}

/** Exact id lookup — preferred, since the API returns the ids alongside the names. */
function findById(options: Option[], id: string | undefined): Option | undefined {
  if (!id || id === '0') return undefined
  return options.find((o) => String(o.id) === String(id))
}

/** Case-insensitive name lookup, mirroring the legacy `option:contains` matching. */
function resolveOption(options: Option[], name: string): Option | undefined {
  if (!name) return undefined
  const needle = name.trim().toLowerCase()
  return options.find((o) => o.name.trim().toLowerCase() === needle)
}

function withFallback(options: Option[], name: string, matched: Option | undefined): Option[] {
  if (matched || !name) return options
  return [...options, { id: name, name }]
}
